(function () {
  const SWIPE_THRESHOLD = 48;

  function isQeviqModal(modal) {
    return modal?.classList?.contains('product-media-modal--qeviq');
  }

  function normalizeMediaId(value) {
    if (value == null || value === '') return null;
    const str = String(value);
    const prefixed = str.match(/-(\d+)$/);
    return prefixed ? prefixed[1] : str;
  }

  function getGallerySlideFromOpener(opener) {
    if (!opener) return null;

    return (
      opener.closest('.product__media-item[data-media-id]') ||
      opener.closest('.qeviq-pdp-media-grid__item[data-media-id]')
    );
  }

  function getVisibleGallerySlide(opener) {
    const gallery = opener?.closest?.('media-gallery');
    const slider = gallery?.querySelector('.product__media-list');
    if (!slider) return null;

    const slides = Array.from(slider.querySelectorAll('.product__media-item[data-media-id]'));
    if (!slides.length) return null;

    const sliderRect = slider.getBoundingClientRect();
    const centerX = sliderRect.left + sliderRect.width / 2;

    let bestSlide = slides[0];
    let bestDistance = Infinity;

    slides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const distance = Math.abs(slideCenter - centerX);

      if (distance < bestDistance) {
        bestDistance = distance;
        bestSlide = slide;
      }
    });

    return bestSlide;
  }

  function resolveMediaIdFromOpener(opener) {
    if (!opener) return null;

    const directId = opener.getAttribute?.('data-media-id');
    if (directId) return normalizeMediaId(directId);

    const slide = getGallerySlideFromOpener(opener);
    if (slide) return normalizeMediaId(slide.getAttribute('data-media-id'));

    const anyMedia = opener.closest?.('[data-media-id]');
    if (anyMedia) return normalizeMediaId(anyMedia.getAttribute('data-media-id'));

    return null;
  }

  function getSectionIdFromModal(modal) {
    if (!modal?.id) return null;
    return modal.id.replace(/^ProductModal-/, '');
  }

  function getModalMediaNodes(modal) {
    const content = modal.querySelector('.product-media-modal__content');
    if (!content) return [];

    return Array.from(content.children).filter((node) => node.hasAttribute('data-media-id'));
  }

  function getMediaItems(modal) {
    return getModalMediaNodes(modal);
  }

  function getActiveIndex(items) {
    return items.findIndex((item) => item.classList.contains('active'));
  }

  function loadActiveDeferredMedia(activeMedia) {
    if (!activeMedia || activeMedia.nodeName !== 'DEFERRED-MEDIA') return;

    const template = activeMedia.querySelector('template');
    const templateContent = template ? template.content : null;
    if (templateContent?.querySelector('.js-youtube')) {
      activeMedia.loadContent();
    }
  }

  function syncGalleryToMedia(modal, mediaId) {
    const sectionId = getSectionIdFromModal(modal);
    const normalizedId = normalizeMediaId(mediaId);
    if (!sectionId || !normalizedId) return;

    const gallery = document.querySelector(`#MediaGallery-${sectionId}`);
    if (!gallery) return;

    const targetKey = `${sectionId}-${normalizedId}`;
    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const slider = viewer?.querySelector('.product__media-list');

    if (slider) {
      slider.querySelectorAll('[data-media-id]').forEach((slide) => {
        slide.classList.toggle('is-active', slide.getAttribute('data-media-id') === targetKey);
      });

      const activeSlide = slider.querySelector(`[data-media-id="${targetKey}"]`);
      if (activeSlide) {
        slider.scrollTo({ left: activeSlide.offsetLeft, behavior: 'auto' });
      }
    }

    const thumbnail = gallery.querySelector(`[data-target="${targetKey}"]`);
    if (thumbnail) {
      gallery.querySelectorAll('.thumbnail-list__item button').forEach((button) => {
        button.removeAttribute('aria-current');
      });
      thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
    }
  }

  function setActiveMedia(modal, mediaId) {
    const normalizedId = normalizeMediaId(mediaId);
    if (!normalizedId) return;

    getModalMediaNodes(modal).forEach((element) => {
      element.classList.toggle('active', normalizeMediaId(element.getAttribute('data-media-id')) === normalizedId);
    });

    const activeMedia = getModalMediaNodes(modal).find(
      (element) => normalizeMediaId(element.getAttribute('data-media-id')) === normalizedId
    );
    loadActiveDeferredMedia(activeMedia);
    modal.qeviqLastMediaId = normalizedId;
    modal.qeviqUpdateNavState?.();
  }

  function unlockPageScroll(scrollY) {
    document.documentElement.classList.remove('qeviq-media-modal-open');
    document.body.classList.remove('qeviq-media-modal-open');
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden-mobile');
    document.body.classList.remove('overflow-hidden-tablet');
    document.body.style.removeProperty('--qeviq-scroll-top');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('height');
    document.body.style.removeProperty('touch-action');
    document.documentElement.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('position');
    document.documentElement.style.removeProperty('height');
    document.documentElement.style.removeProperty('touch-action');

    const y = Math.max(0, Number(scrollY) || 0);
    const restore = () => {
      window.scrollTo(0, y);
      document.documentElement.scrollTop = y;
    };

    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  }

  function patchGalleryModalOpeners() {
    if (document.documentElement.dataset.qeviqGalleryModalOpenersPatched) return;
    document.documentElement.dataset.qeviqGalleryModalOpenersPatched = 'true';

    let lastTouchOpenAt = 0;
    let galleryTouchStartX = null;
    let galleryTouchStartY = null;

    document.addEventListener(
      'touchstart',
      (event) => {
        const opener = event.target.closest('.product-info--qeviq modal-opener[data-modal]');
        if (!opener || event.touches.length !== 1) {
          galleryTouchStartX = null;
          galleryTouchStartY = null;
          return;
        }

        galleryTouchStartX = event.touches[0].clientX;
        galleryTouchStartY = event.touches[0].clientY;
      },
      true
    );

    function openGalleryModal(event) {
      const opener = event.target.closest('.product-info--qeviq modal-opener[data-modal]');
      if (!opener) return;

      const modalSelector = opener.getAttribute('data-modal');
      const modal = modalSelector ? document.querySelector(modalSelector) : null;
      if (!isQeviqModal(modal)) return;

      if (event.type === 'click' && Date.now() - lastTouchOpenAt < 700) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      if (event.type === 'touchend') {
        if (galleryTouchStartX != null && event.changedTouches.length === 1) {
          const deltaX = event.changedTouches[0].clientX - galleryTouchStartX;
          const deltaY = event.changedTouches[0].clientY - (galleryTouchStartY || 0);
          galleryTouchStartX = null;
          galleryTouchStartY = null;

          if (Math.abs(deltaX) > 12 || Math.abs(deltaY) > 12) return;
        }

        lastTouchOpenAt = Date.now();
      }

      const slide = getGallerySlideFromOpener(opener) || getVisibleGallerySlide(opener);
      const trigger =
        slide ||
        event.target.closest('[data-media-id]') ||
        opener.querySelector('[data-media-id]');
      if (!trigger) return;

      if (event.type === 'touchend') {
        lastTouchOpenAt = Date.now();
        document.dispatchEvent(new CustomEvent('qeviq:gallery-modal-open'));
        modal.show(trigger);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      document.dispatchEvent(new CustomEvent('qeviq:gallery-modal-open'));
      modal.show(trigger);
    }

    document.addEventListener('click', openGalleryModal, true);
    document.addEventListener('touchend', openGalleryModal, true);
  }

  function patchProductModal() {
    const ProductModal = customElements.get('product-modal');
    if (!ProductModal?.prototype || ProductModal.prototype.__qeviqMediaModalPatched) return false;

    const originalShow = ProductModal.prototype.show;
    const originalHide = ProductModal.prototype.hide;
    const originalShowActiveMedia = ProductModal.prototype.showActiveMedia;
    const modalDialogShow = Object.getPrototypeOf(ProductModal.prototype).show;

    ProductModal.prototype.show = function show(opener) {
      if (!isQeviqModal(this)) {
        originalShow.call(this, opener);
        return;
      }

      this.qeviqScrollY = window.scrollY || document.documentElement.scrollTop || 0;

      modalDialogShow.call(this, opener);

      document.documentElement.classList.add('qeviq-media-modal-open');
      document.body.classList.add('qeviq-media-modal-open');

      this.qeviqBindEvents?.();
      this.qeviqShowActiveMediaWithoutScroll?.(opener || this.openedBy);
      this.qeviqUpdateNavState?.();
    };

    ProductModal.prototype.hide = function hide() {
      if (isQeviqModal(this)) {
        const scrollY = this.qeviqScrollY ?? window.scrollY ?? 0;
        const lastMediaId = this.qeviqLastMediaId;

        getModalMediaNodes(this).forEach((element) => element.classList.remove('active'));
        this.qeviqUnbindEvents?.();

        originalHide.call(this);

        unlockPageScroll(scrollY);
        document.dispatchEvent(new CustomEvent('qeviq:gallery-modal-closed'));

        if (lastMediaId) {
          syncGalleryToMedia(this, lastMediaId);
        }

        return;
      }

      originalHide.call(this);
    };

    ProductModal.prototype.showActiveMedia = function showActiveMedia() {
      if (!isQeviqModal(this)) {
        originalShowActiveMedia.call(this);
        return;
      }

      this.qeviqShowActiveMediaWithoutScroll?.(this.openedBy);
    };

    ProductModal.prototype.qeviqShowActiveMediaWithoutScroll = function qeviqShowActiveMediaWithoutScroll(opener) {
      const mediaId = resolveMediaIdFromOpener(opener || this.openedBy);
      if (!mediaId) return;

      setActiveMedia(this, mediaId);
    };

    ProductModal.prototype.qeviqUpdateNavState = function qeviqUpdateNavState() {
      const items = getMediaItems(this);
      const activeIndex = getActiveIndex(items);
      const prev = this.querySelector('[data-qeviq-modal-prev]');
      const next = this.querySelector('[data-qeviq-modal-next]');

      if (prev) prev.disabled = activeIndex <= 0;
      if (next) next.disabled = activeIndex < 0 || activeIndex >= items.length - 1;

      this.querySelectorAll('[data-qeviq-modal-media-id]').forEach((mark) => {
        const isActive =
          normalizeMediaId(mark.getAttribute('data-qeviq-modal-media-id')) ===
          normalizeMediaId(items[activeIndex]?.getAttribute('data-media-id'));
        mark.classList.toggle('is-active', isActive);
        if (isActive) {
          mark.setAttribute('aria-current', 'true');
        } else {
          mark.removeAttribute('aria-current');
        }
      });
    };

    ProductModal.prototype.qeviqNavigate = function qeviqNavigate(direction) {
      const items = getMediaItems(this);
      const activeIndex = getActiveIndex(items);
      if (activeIndex < 0) return;

      const targetIndex = activeIndex + direction;
      if (targetIndex < 0 || targetIndex >= items.length) return;

      const target = items[targetIndex];
      setActiveMedia(this, target.getAttribute('data-media-id'));
      window.pauseAllMedia?.();
    };

    ProductModal.prototype.qeviqOnKeyDown = function qeviqOnKeyDown(event) {
      if (!this.hasAttribute('open')) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.qeviqNavigate(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.qeviqNavigate(1);
          break;
        default:
          break;
      }
    };

    ProductModal.prototype.qeviqOnTouchStart = function qeviqOnTouchStart(event) {
      if (event.touches.length !== 1) return;
      this.qeviqTouchStartX = event.touches[0].clientX;
      this.qeviqTouchStartY = event.touches[0].clientY;
      this.qeviqTouchStartedOnNav = !!event.target.closest(
        '[data-qeviq-modal-prev], [data-qeviq-modal-next], [data-qeviq-modal-progress], .product-media-modal__close'
      );
    };

    ProductModal.prototype.qeviqOnTouchEnd = function qeviqOnTouchEnd(event) {
      if (this.qeviqTouchStartedOnNav) {
        this.qeviqTouchStartedOnNav = false;
        this.qeviqTouchStartX = null;
        this.qeviqTouchStartY = null;
        return;
      }

      if (this.qeviqTouchStartX == null || event.changedTouches.length !== 1) return;

      const deltaX = event.changedTouches[0].clientX - this.qeviqTouchStartX;
      const deltaY = event.changedTouches[0].clientY - (this.qeviqTouchStartY || 0);

      this.qeviqTouchStartX = null;
      this.qeviqTouchStartY = null;

      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

      if (deltaX > 0) {
        this.qeviqNavigate(-1);
      } else {
        this.qeviqNavigate(1);
      }
    };

    ProductModal.prototype.qeviqOnNavClick = function qeviqOnNavClick(event, direction) {
      event.preventDefault();
      event.stopPropagation();

      if (event.type === 'touchend') {
        this.qeviqLastNavTouchAt = Date.now();
      } else if (event.type === 'click' && Date.now() - (this.qeviqLastNavTouchAt || 0) < 700) {
        return;
      }

      this.qeviqNavigate(direction);
    };

    ProductModal.prototype.qeviqBindEvents = function qeviqBindEvents() {
      if (this.qeviqEventsBound) return;
      this.qeviqEventsBound = true;

      this.qeviqOnKeyDownHandler = this.qeviqOnKeyDown.bind(this);
      this.qeviqOnTouchStartHandler = this.qeviqOnTouchStart.bind(this);
      this.qeviqOnTouchEndHandler = this.qeviqOnTouchEnd.bind(this);
      this.qeviqOnPrevHandler = (event) => this.qeviqOnNavClick(event, -1);
      this.qeviqOnNextHandler = (event) => this.qeviqOnNavClick(event, 1);
      this.qeviqOnProgressHandler = (event) => {
        const mark = event.target.closest('[data-qeviq-modal-media-id]');
        if (!mark) return;
        event.preventDefault();
        setActiveMedia(this, mark.getAttribute('data-qeviq-modal-media-id'));
        window.pauseAllMedia?.();
      };

      this.addEventListener('keydown', this.qeviqOnKeyDownHandler);

      const touchSurface =
        this.querySelector('.product-media-modal__dialog') ||
        this.querySelector('[data-qeviq-modal-content]') ||
        this;
      this.qeviqTouchSurface = touchSurface;
      touchSurface.addEventListener('touchstart', this.qeviqOnTouchStartHandler, { passive: true });
      touchSurface.addEventListener('touchend', this.qeviqOnTouchEndHandler, { passive: true });

      const prev = this.querySelector('[data-qeviq-modal-prev]');
      const next = this.querySelector('[data-qeviq-modal-next]');
      const progress = this.querySelector('[data-qeviq-modal-progress]');
      prev?.addEventListener('click', this.qeviqOnPrevHandler);
      next?.addEventListener('click', this.qeviqOnNextHandler);
      progress?.addEventListener('click', this.qeviqOnProgressHandler);
    };

    ProductModal.prototype.qeviqUnbindEvents = function qeviqUnbindEvents() {
      if (!this.qeviqEventsBound) return;
      this.qeviqEventsBound = false;

      this.removeEventListener('keydown', this.qeviqOnKeyDownHandler);

      const touchSurface = this.qeviqTouchSurface || this.querySelector('[data-qeviq-modal-content]') || this;
      touchSurface.removeEventListener('touchstart', this.qeviqOnTouchStartHandler);
      touchSurface.removeEventListener('touchend', this.qeviqOnTouchEndHandler);

      const prev = this.querySelector('[data-qeviq-modal-prev]');
      const next = this.querySelector('[data-qeviq-modal-next]');
      const progress = this.querySelector('[data-qeviq-modal-progress]');
      prev?.removeEventListener('click', this.qeviqOnPrevHandler);
      next?.removeEventListener('click', this.qeviqOnNextHandler);
      progress?.removeEventListener('click', this.qeviqOnProgressHandler);
    };

    ProductModal.prototype.__qeviqMediaModalPatched = true;
    return true;
  }

  function init() {
    patchGalleryModalOpeners();

    if (!patchProductModal()) {
      window.setTimeout(init, 50);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
